import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { track } from '@amplitude/analytics-browser';

import {
  Breakpoint,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
} from '@mui/material';

import {
  authContext,
  useGetJobPostings,
} from '@staffedupusa/shared-components';

import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';
import { PositionOption, useGetPositions } from '@features/positions';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import CompletionModal from '@features/accountCompletion/components/CompletionModal';

import getOnboardingStepName from '@utils/getOnboardingStepName';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

import AddPositionsStep from '../AddPositionsStep/AddPositionsStep';
import AddQuestionsStep from '../AddQuestionsStep/AddQuestionsStep';
import PickPlanStep from '../PickPlanStep/PickPlanStep';
import SelectIndustryStep from '../SetIndustryStep/SetIndustryStep';
import WelcomeStep from '../WelcomeStep/WelcomeStep';
import OnboardingStepTitiles from '../OnboardingStepTitiles/OnboardingStepTitiles';
import OnboardingStepContent from '../OnboardingStepContent/OnboardingStepContent';
import { stepsArray } from '@features/accountCompletion/hooks/constants';
import { useGetCurrentPlan } from '@features/payments/hooks/useGetCurrentPlan';
import { staticFileLocation } from '@server/constants';

type Step = {
  label: string;
  subtitle?: string;
  subtitle2?: string;
  content: ReactNode;
  nextLabel: string;
  disableNext?: boolean;
  modalMaxWidth?: Breakpoint;
  showLogo?: boolean;
};

const EmployerOnboardingModal = () => {
  const [errorText, setErrorText] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] =
    useState<boolean>(false);
  const [isYouSureModalOpen, setIsYouSureModalOpen] = useState<boolean>(false);
  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [hasQuestions, setHasQuestion] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [comeFromPreview, setComeFromPreview] = useState<boolean>(false);
  const [industryId, setIndustryId] = useState<number>(0);
  const [jobsLength, setJobsLength] = useState<number>(0);
  const { paymentInfo } = useGetPaymantDetails();
  const { authData } = useContext(authContext);
  const { jobPostingsData } = useGetJobPostings();
  const [options, setOptions] = useState<PositionOption[]>([]);
  const { updateProfile } = useUpdateProfile({
    setErrorText,
  });
  const { currentPlanData, isCurrentPlanLoading } = useGetCurrentPlan();
  useGetPositions({
    setPositions: setOptions,
    industryId,
  });
  const handleNext = () => {
    if (
      (paymentInfo?.payment === 1 || paymentInfo?.payment === 2) &&
      activeStep === 6
    ) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 7 || activeStep > 7) {
      handleCloseAndResetModal();
    } else if (activeStep == 2 && comeFromPreview) {
      setActiveStep(5);
      setComeFromPreview(false);
    } else if (activeStep === 4) {
      updateProfile({
        walkthrough_completed: true,
        has_payment_method: true,
      });
      handleCloseAndResetModal();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep - 1 < steps.length ? prevActiveStep - 1 : prevActiveStep,
    );
  };

  const handleCloseAndResetModal = () => {
    setIsOnboardingModalOpen(false);
    setIsClosed(true);
    setActiveStep(0);
  };

  const handleCloseModal = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setIsOnboardingModalOpen(false);
      setIsYouSureModalOpen(true);
    }
  };

  useEffect(() => {
    const dialogContent = document.querySelector('.MuiDialogContent-root');
    if (dialogContent) {
      dialogContent.scrollTop = 0;
    }
    const amplitudeEvent = {
      eventType: amplitudeEventsName.VIEWD_ONBOARDING_STEPS,
      eventProperties: {
        ...(authData.user && { userId: authData.user?.id }),
        'onboarding step': getOnboardingStepName(activeStep),
      },
    };
    isOnboardingModalOpen &&
      track(amplitudeEvent.eventType, amplitudeEvent.eventProperties);
  }, [activeStep]);

  const steps: Step[] = [
    {
      label: 'Let’s  post your job! 🎉',
      content: <WelcomeStep />,
      nextLabel: 'Let’s Go!',
      modalMaxWidth: 'xs',
      showLogo: true,
    },
    {
      label: 'What industry are you in?',
      subtitle:
        'We’ll customize your profile with hiring templates specifically <br/> for your industry, created by experts in your field.',
      content: (
        <SelectIndustryStep handleNext={handleNext} industryId={industryId} />
      ),
      nextLabel: 'Next!',
      disableNext: !industryId,
    },
    {
      label: 'What job would you like to post?',
      subtitle:
        'Pick your position, & create your first job posting in seconds.',
      subtitle2:
        'Use our pre-drafted descriptions & customize as needed, <br/> then simply review & publish as soon as you’re ready!',
      content: (
        <AddPositionsStep
          handleGoBack={handleBack}
          industryId={industryId}
          setJobsLength={setJobsLength}
          handleNext={handleNext}
        />
      ),
      disableNext: !jobPostingsData || jobPostingsData?.object?.length === 0,
      nextLabel: 'Next!',
    },
    {
      label: 'Want to ask your applicants <br/> any specific questions?',
      subtitle: `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#00d26a">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.59l-4.59-4.59L6 10l4 4 8-8 1.41 1.41L10 16.59z"/>
        </svg> 
        Pick from our pre-drafted frequently used <br/> 
        questions, or create your own.<br/><br/> 
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#00d26a">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.59l-4.59-4.59L6 10l4 4 8-8 1.41 1.41L10 16.59z"/>
        </svg> 
        Set questions as pre-qualifiers to help hire a better<br/> 
        team in a fraction of the time.
  `,
      content: (
        <AddQuestionsStep
          setHasQuestion={setHasQuestion}
          hasQuestions={hasQuestions}
        />
      ),
      nextLabel: hasQuestions ? 'All set' : 'Skip & Continue',
      modalMaxWidth: 'sm',
    },
    {
      label: 'Simple Billing - Easy Hiring - Great Hires',
      content:
        authData?.user?.company?.is_company_paid &&
        currentPlanData?.status !== 200 ? (
          <PickPlanStep handleNext={handleNext} />
        ) : currentPlanData?.status === 200 &&
          currentPlanData.object &&
          !authData?.user?.company?.enterprise_company ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={3}
            sx={{ maxWidth: 500, textAlign: 'center', mx: 'auto' }}
          >
            <img
              alt="Subscription Active"
              src={`${staticFileLocation}img/logos/LogoStampShadow.png`}
              style={{ width: '100px', height: '100px', objectFit: 'contain' }}
            />
            <Typography
              variant="h4"
              color="gray"
              sx={{ alignSelf: 'center', textAlign: 'center' }}
            >
              {authData?.user?.company?.is_company_paid ? (
                <>
                  You’re all set! You have an active subscription on the{' '}
                  {currentPlanData.object.plan_int === 1
                    ? 'smarter'
                    : currentPlanData.object.plan_int === 2
                      ? 'pro'
                      : currentPlanData.object.plan_int === 4 && 'starter'}
                  plan. If you need to upgrade or change your plan, visit your.
                  account settings.
                </>
              ) : (
                <>
                  Your subscription is already active on the{' '}
                  {currentPlanData.object.plan_int === 1
                    ? 'smarter'
                    : currentPlanData.object.plan_int === 2
                      ? 'pro'
                      : currentPlanData.object.plan_int === 4 && 'starter'}{' '}
                  plan and is managed by your enterprise. No further action is
                  needed.
                </>
              )}
            </Typography>
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{ width: '100%' }}
            >
              Finish
            </Button>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={3}
            sx={{ maxWidth: 500, textAlign: 'center', mx: 'auto' }}
          >
            <img
              alt="Subscription Active"
              src={`${staticFileLocation}img/logos/LogoStampShadow.png`}
              style={{ width: '100px', height: '100px', objectFit: 'contain' }}
            />

            <Typography variant="h5" color="gray">
              Your subscription is already active and managed by your
              enterprise. No further action is needed.
            </Typography>

            <Button
              onClick={handleNext}
              variant="contained"
              sx={{ width: '100%' }}
            >
              Finish
            </Button>
          </Box>
        ),
      modalMaxWidth:
        authData?.user?.company?.is_company_paid &&
        currentPlanData?.status !== 200
          ? 'md'
          : 'xs',
      nextLabel: 'Next',
      disableNext:
        authData?.user?.company?.is_company_paid &&
        currentPlanData?.status !== 200
          ? true
          : false,
    },
  ];

  const checkOnboardingStep = () => {
    if (authData?.user && authData?.user?.company) {
      return stepsArray.findIndex((item, index) => {
        const isComplete =
          authData?.user &&
          authData.user[item.completionProperty as keyof typeof authData.user];
        return !isComplete;
      });
    }
    return 0;
  };

  useEffect(() => {
    if (
      authData?.user &&
      !authData.user?.walkthrough_completed &&
      authData.user?.master_account &&
      authData.user?.walkthrough_status < 1 &&
      !isYouSureModalOpen &&
      !isClosed
    ) {
      const onboardingStep = checkOnboardingStep();
      if (typeof onboardingStep === 'number' && onboardingStep !== -1) {
        if ([0, 1].includes(onboardingStep)) {
          setIsOnboardingModalOpen(true);
          setActiveStep(0);
        }
        if ([2].includes(onboardingStep)) {
          setIsOnboardingModalOpen(true);
          setActiveStep(3);
        } else if ([3].includes(onboardingStep)) {
          setIsOnboardingModalOpen(true);
          setActiveStep(4);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (authData?.user?.company?.industries?.length) {
      setIndustryId(authData.user?.company.industries[0].id);
    }
  }, [authData]);
  return (
    <>
      <Dialog
        open={isOnboardingModalOpen}
        onClose={handleCloseModal}
        maxWidth={steps[activeStep].modalMaxWidth || 'sm'}
        fullWidth
      >
        <DialogTitle>
          <OnboardingStepTitiles activeStep={activeStep} steps={steps} />
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '30px' }}>
          <OnboardingStepContent
            activeStep={activeStep}
            steps={steps}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        </DialogContent>
      </Dialog>
      <CompletionModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
    </>
  );
};

export default EmployerOnboardingModal;
